import { IIntent, ISchedule } from '@lifetools/shared-app-data';
import { createLookup } from 'utils/array/createLookup';
import { StoredScheduleBlock } from './StoredScheduleBlock';

/**
 * A stored schedule model together with the intents its blocks resolve to,
 * keyed by todo id.
 */
export interface StoredModel {
  model: StoredScheduleBlock[];
  intentsByTodoId: Record<string, IIntent>;
}

/**
 * Projects a schedule and its intents to the stored schedule model the layout
 * engine renders from.
 *
 * When the schedule has a persisted `views.schedule.blocks`, that IS the model:
 * every stored block is carried — including blocks whose todo hasn't loaded yet —
 * so persisting an edit derived from this model can never silently drop a block
 * the renderer happened to skip. The stored block's `start` is authoritative over
 * the intent's own `start`.
 *
 * Schedules without a stored view derive their model from the scheduled intents
 * directly, in chronological order.
 */
export function toStoredModel(schedule: ISchedule, intents: IIntent[]): StoredModel {
  const viewBlocks = schedule.views?.schedule?.blocks;

  if (viewBlocks) {
    const intentsById = createLookup(intents);
    const intentsByTodoId: Record<string, IIntent> = {};

    for (const block of viewBlocks) {
      const intent = intentsById[`${schedule.id}_${block.todo}`];

      if (intent) {
        intentsByTodoId[`${block.todo}`] = intent;
      }
    }

    return {
      model: viewBlocks.map(block => ({
        todo: `${block.todo}`,
        ...(block.start != null ? { start: `${block.start}` } : {}),
      })),
      intentsByTodoId,
    };
  }

  const scheduled = intents
    .filter(intent => intent.start && intent.todo?.id)
    .sort((a, b) => `${a.start}`.localeCompare(`${b.start}`));

  const model: StoredScheduleBlock[] = scheduled.map(intent => ({
    todo: `${intent.todo!.id}`,
    start: `${intent.start}`,
  }));

  return {
    model,
    intentsByTodoId: Object.fromEntries(
      scheduled.map(intent => [`${intent.todo!.id}`, intent]),
    ),
  };
}
